import { CardIssueType, IdCardType, PointTransactionType, PurchaseType } from '@models/Enums';
import { CustomerModel } from '@models/CustomerModel';
import { VendorStoreCustomer } from '@models/VendorModel';
import { TransactionModel } from '@models/TransactionModel';
import { SessionManager } from '@services/session-manager';


export class SessionModel {

    public constructor(init?: Partial<Pick<SessionModel, keyof SessionModel>>) {

        if (init) {
            if (init.user) {
                init.user = new CustomerModel(init.user);
            }
            if (init.giviScanSession) {
                init.giviScanSession = new GiviScanSessionModel(init.giviScanSession);
            }
            Object.assign(this, init);
        }
    }

    accessToken: string;
    refreshToken: string;

    firstAccess: { couponId: number; accessDate: Date; }[] = [];


    user: CustomerModel;

    giviScanSession: GiviScanSessionModel;

    selectedStore: VendorStoreCustomer = null;


    verifyGiviIssues(givis: TransactionModel[]): { giviId: number, issueId?: number, token: string, type: CardIssueType }[] {

        const issueTypes: { giviId: number, issueId?: number, token: string, type: CardIssueType }[] = [];

        if (givis?.length) {

            const purchases = givis.filter(x => x.type == PointTransactionType.PurchaseGiftcards && x.redeemedAt == null);
            
            // Si todas las tarjetas están verificadas, no se validan los triggers
            if (purchases.every(x => x.customerCard?.verifiedAt)) {
                //no verification needed
                return issueTypes;
            }

            // Validar cada compra para ver si esta disponibles
            // Triggers 1 al 4
            purchases.forEach(purchase => {

                const issues = this.getPurchaseCardIssueTypes(purchase);
                issues?.forEach(x => issueTypes.push(x));
            });

            // Trigger 5: if the user have more than 2 purchases today
            const recent = SessionManager.getRecentPurchases()
            if (recent?.length >= 2) {

                // Se valida que la tarjeta no se haya agregado a la lista de issue por otro type para que no este doble en la lista
                if (!issueTypes.some(x => x.token == recent[0].cardToken)) {
                    issueTypes.push({
                        giviId: recent[0].id,
                        token: recent[0].cardToken,
                        type: CardIssueType.OverTwoPurchases
                    });
                }
            }
        }

        if (this.user?.cards?.length) {

            // Busca si las tarjetas tienen casos abiertos
            this.user?.cards.forEach(card => {
                card.issues?.forEach(issue => {
                    // Caso abierto
                    if (issue.verifiedAt == null && issue.rejectedAt == null) {
                        issueTypes.push({
                            giviId: null,
                            issueId: issue.id,
                            token: issue.token,
                            type: issue.type
                        });
                    }
                });
            });
        }

        return issueTypes;
    }


    private getPurchaseCardIssueTypes(givi: TransactionModel): { giviId: number, issueId?: number, token: string, type: CardIssueType }[] {

        let issueTypes: { giviId: number, token: string, type: CardIssueType }[] = [];

        // Trigger 1: higher than 50000
        if (givi.subTotal >= 50000) {

            issueTypes.push({
                giviId: givi.id,
                token: givi.cardToken,
                type: CardIssueType.PurchaseMoreThan50k
            });
        }

        // Trigger 2: purchase after 24 hours of create account
        if (givi.customer.createdAt && givi.paymentAt) {

            // Omite las compras en el checkout express y se crea la cuenta ahi mismo
            if (givi.purchaseType != PurchaseType.GiviWebCheckoutExpress && givi.purchaseType != PurchaseType.HolaGiviCheckoutExpress) {

                // calculate the dif and convert it into hours, 360000ms
                const differenceInHours = Math.abs(givi.paymentAt.getTime() - givi.customer.createdAt.getTime()) / 36e5;

                // Verificamos si la diferencia es menor a 24 horas
                if (differenceInHours <= 24) {
                    issueTypes.push({
                        giviId: givi.id,
                        token: givi.cardToken,
                        type: CardIssueType.SameDayAccountAndPurchase
                    });
                }
            }
        }

        // Trigger 3: Givi schedule: before 11pm after 6am, no verification needed
        if ((new Date().getHours() >= 23 || new Date().getHours() < 6)) {

            issueTypes.push({
                giviId: givi.id,
                token: givi.cardToken,
                type: CardIssueType.OutOfTime
            });
        }

        // Trigger 4: same customer name and card first name, no verification needed
        if (givi.customer.name != null && givi.customerCard?.owner != null) {

            const firstName = givi.customerCard.owner.split(' ')[0].toLowerCase();
            const customerName = givi.customer.name.toLowerCase();

            if (customerName !== firstName) {
                issueTypes.push({
                    giviId: givi.id,
                    token: givi.cardToken,
                    type: CardIssueType.DifferentCardholder
                });
            }
        }

        // Filtrar issues únicos por compra
        issueTypes = issueTypes.filter((issue, index, self) => {
            return index === self.findIndex(x => x.giviId === issue.giviId);
        });

        return issueTypes;
    }
}

export class UserModel {

    public constructor(init?: Partial<Pick<UserModel, keyof UserModel>>) {

        if (init) {
            Object.assign(this, init);
        }
    }

    id = 0;
    companyId = 0;
    username = '';
    name = '';
    lastName = '';
    isAdmin: boolean;
    email = '';
    phone = '';
    birthDate: Date;

    idCardType: IdCardType = IdCardType.Fisica;
    idCard = '';

    province = 0;
    canton = 0;
    district = 0;

    logoURL = '';

    expirationPointsAt = new Date(2025, 11, 31);

    get fullname(): string {

        return `${this.name} ${this.lastName}`.trim();
    }
}

export class GiviScanSessionModel {

    public constructor(init?: Partial<Pick<GiviScanSessionModel, keyof GiviScanSessionModel>>) {

        if (init) {
            if (init.user) {
                init.user = new CustomerModel(init.user);
                if (init.vendorStores && init.vendorStores.length) {
                    init.vendorStores = init.vendorStores.map(x => new VendorStoreCustomer(x));
                }
            }
            Object.assign(this, init);
        }
    }

    accessToken: string;
    refreshToken: string;
    userId: number;

    vendorStores: VendorStoreCustomer[] = [];
    selectedStore: VendorStoreCustomer = null;

    user: CustomerModel;
}


